/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import '../bids/CreateBid.css';

const UserProfileForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    contactNote: '',
    username: '',
    managerName: '',
    companyName: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.getMyProfile();

      if (!response.success || !response.participant) {
        setError('Failed to load profile');
        return;
      }

      const participant = response.participant;
      setFormData({
        firstName: participant.firstName || '',
        lastName: participant.lastName || '',
        title: participant.title || '',
        contactNote: participant.contactNote || '',
        username: participant.username || '',
        managerName: participant.managerName || 'None',
        companyName: participant.companyName || 'None',
      });
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSaving(true);

    try {
      const request = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        contactNote: formData.contactNote,
      };

      const response = await apiClient.updateMyProfile(request);

      if (response.success) {
        setSuccessMessage('Profile updated successfully');
        // Reload profile to show updated data
        setTimeout(() => loadProfile(), 1000);
      } else {
        setError(response.error || 'Failed to update profile');
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while updating profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="create-bid-container">
      <BackButton to="/" />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !formData.username) {
    return (
      <div className="create-bid-container">
        <div className="error-message">
          {error}
        </div>
        <button
          type="button"
          onClick={handleCancel}
          className="cancel-button"
          style={{ marginTop: '1rem' }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="create-bid-container">
      <div className="create-bid-header">
        <h2>Edit My Profile</h2>
      </div>

      <form onSubmit={handleSubmit} className="create-bid-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '6px',
            color: '#155724'
          }}>
            {successMessage}
          </div>
        )}

        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            value={formData.username}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Username cannot be changed
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            disabled={isSaving}
          />
        </div>

        <div className="form-group">
          <label htmlFor="contactNote">Contact Notes</label>
          <textarea
            id="contactNote"
            name="contactNote"
            value={formData.contactNote}
            onChange={handleChange}
            rows={4}
            disabled={isSaving}
            style={{ minHeight: '100px', resize: 'vertical' }}
          />
        </div>

        <div className="form-group">
          <label>Manager</label>
          <input
            type="text"
            value={formData.managerName}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Contact an administrator to change your manager
          </small>
        </div>

        <div className="form-group">
          <label>Company</label>
          <input
            type="text"
            value={formData.companyName}
            readOnly
            disabled
            style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
          />
          <small style={{ color: '#666', fontSize: '0.85rem' }}>
            Contact an administrator to change your company
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="submit-button"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfileForm;
